import { gsap } from 'gsap';

const store_id = 'Xi8KgyMctkoGKyOqXd9GzbzRt5EdIxkGIdtU5JOm'

const selectors = {
  reviewCount: '[data-review-count]',
  template: '[data-review-template]',
  reviewList: '[data-review-list]',
  reviewSort: '[data-review-sort]',
  loadMoreButton: '[data-load-more-reviews]',
  loadMoreContainer: '[data-load-more-reviews-container]',
  reviewFilter: '[data-review-filter]',
  reviewFilterLabel: '[data-review-filter-label]',
}

const templateSelectors = {
  reviewItem: '[data-review-item]',
  reviewTitle: '[data-review-title]',
  reviewBody: '[data-review-body]',
  reviewImageContainer: '[data-review-image-container]',
  reviewImage: '[data-review-image]',
  reviewAvatar: '[data-review-avatar]',
  reviewUser: '[data-review-user]',
  reviewVerified: '[data-review-verified]',
  reviewDate: '[data-review-date]',
  reviewRating: '[data-review-rating]',
  reviewRatingStar: '[data-rating-star]',
  reviewComment: '[data-review-comment]',
  reviewCommentBody: '[data-review-comment-body]',
}

const MAX_PER_PAGE = 3;

class ProductReviews extends HTMLElement {
  #productId;
  #data;
  #reviewCount;
  #template;
  #reviewList;
  #reviewSort;
  #loadMoreButton;
  #loadMoreContainer;
  #reviewFilter;
  #reviewFilterLabel;
  #currentPage = 1;
  #sortBy = 'rating-desc';
  #filter = 'all';

  constructor() {
    super();

    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.getQueryParams = this.getQueryParams.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.fetchReviews = this.fetchReviews.bind(this);
    this.render = this.render.bind(this);
  }

  handleFilterChange(event) {
    const value = event.currentTarget.value;

    this.#filter = value;

    if (this.#filter === 'all') {
      this.#reviewFilterLabel.textContent = 'All';
    } else {
      this.#reviewFilterLabel.textContent = `${this.#filter} Stars`;
    }

    this.fetchReviews();
  }

  updateFilterOptions() {
    if (!this.#reviewFilter) return;

    const distribution = this.#data?.distribution ?? {};

    const filterOptions = this.#reviewFilter.querySelectorAll('option');

    filterOptions?.forEach(option => {
      const value = option.value;

      if (value === 'all') return;

      const reviewCount = distribution[option.value] ?? 0;
      
      if (reviewCount > 0) {
        option.disabled = false;
      } else {
        option.disabled = true;
      }
    })
  }

  handleSortChange(event) {
    const value = event.currentTarget.value;

    this.#sortBy = value;

    this.fetchReviews();
  }

  updateLoadMoreButton() {
    if (!this.#loadMoreButton || !this.#loadMoreContainer) return;

    if (this.#currentPage < (this.#data?.totalPages ?? 1)) {
      gsap.to(this.#loadMoreContainer, {
        height: 'auto',
        autoAlpha: 1,
        duration: 0.65,
        ease: 'expo.out'
      })
    } else {
      gsap.to(this.#loadMoreContainer, {
        height: 0,
        autoAlpha: 0,
        duration: 0.65,
        ease: 'expo.out'
      })
    }
  }

  handleLoadMore(event) {
    event.preventDefault();

    if (this.#currentPage >= this.#data.totalPages) return;

    this.#currentPage++;

    this.fetchReviews(false);
  }

  getRelativeDate(date) {
    if (!date) return;

    const formatter = new Intl.RelativeTimeFormat('en', {
      numeric: 'auto',
    });

    let relativeDate = '';

    const now = new Date().getTime();
    const then = new Date(date).getTime();
    const diffMs = then - now;
    
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    const diffWeeks = diffMs / (1000 * 60 * 60 * 24 * 7);
    const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);
    const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365);

    if (Math.abs(diffHours) < 24) {
      relativeDate = formatter.format(Math.round(diffHours), 'hours');
    }

    else if (Math.abs(diffDays) < 7) {
      relativeDate = formatter.format(Math.round(diffDays), 'days');
    }

    else if (Math.abs(diffDays) < 30) {
      relativeDate = formatter.format(Math.round(diffWeeks), 'weeks');
    }
    
    else if (Math.abs(diffDays) < 365) {
      relativeDate = formatter.format(Math.round(diffMonths), 'months');
    }
    
    else {
      relativeDate = formatter.format(Math.round(diffYears), 'years');
    }

    return relativeDate.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }

  renderReviews(removeExisting = true) {
    if (removeExisting) {
      this.#reviewList.innerHTML = '';
    }

    this.#data.reviews.forEach(review => {
      const { title, comment, content, user, created_at, images_data, verified_buyer, score } = review;

      const template = this.#template.content.cloneNode(true);
      const reviewItem = template.querySelector(templateSelectors.reviewItem);
      const reviewTitle = template.querySelector(templateSelectors.reviewTitle);
      const reviewBody = template.querySelector(templateSelectors.reviewBody);
      const reviewImageContainer = template.querySelector(templateSelectors.reviewImageContainer);
      const reviewImage = template.querySelector(templateSelectors.reviewImage);
      const reviewAvatar = template.querySelector(templateSelectors.reviewAvatar);
      const reviewVerified = template.querySelector(templateSelectors.reviewVerified);
      const reviewUser = template.querySelector(templateSelectors.reviewUser);
      const reviewDate = template.querySelector(templateSelectors.reviewDate);
      const reviewRating = template.querySelector(templateSelectors.reviewRating);
      const reviewStars = reviewRating?.querySelectorAll(templateSelectors.reviewRatingStar);
      const reviewComment = template.querySelector(templateSelectors.reviewComment);
      const reviewCommentBody = template.querySelector(templateSelectors.reviewCommentBody);

      if (!removeExisting && reviewItem) {
        reviewItem.style.height = '0';
      }

      if (reviewStars) {
        reviewStars.forEach(star => {
          const rating = Number(star.dataset.ratingStar ?? 0);

          if (rating > score) {
            star.classList.add('rating-star--empty');
          } else if (rating < score && rating > score - 1) {
            star.classList.add('rating-star--half');
          } else {
            star.classList.add('rating-star--full');
          }
        });
      }

      if (images_data) {
        const url = images_data[0]?.original_url;
        reviewImage.src = url;
      } else {
        reviewImageContainer.remove();
      }

      if (!verified_buyer) {
        reviewVerified.remove();
      }

      if (comment?.content) {
        reviewCommentBody.textContent = comment.content;
      } else {
        reviewComment.remove();
      }

      reviewTitle.textContent = title;
      reviewBody.textContent = content;
      reviewAvatar.textContent = user?.display_name?.charAt(0)
      reviewUser.textContent = user?.display_name;
      reviewDate.textContent = this.getRelativeDate(created_at);

      this.#reviewList.appendChild(template);
    });
  }

  render(removeExisting = true) {
    if (!this.#data || !this.#reviewList) return;

    const hasExistingReviews = this.#reviewList.hasChildNodes();

    this.updateLoadMoreButton();
    this.updateFilterOptions();

    if (this.#reviewCount) {
      if (this.#data.total === 1) {
        this.#reviewCount.textContent = `${this.#data.total} review`;
      } else {
        this.#reviewCount.textContent = `${this.#data.total} reviews`;
      }
    }

    if (!hasExistingReviews) {
      gsap.set(this.#reviewList, { height: this.#reviewList.offsetHeight });
      this.renderReviews(removeExisting);

      requestAnimationFrame(() => {
        gsap.from('.product-reviews__review', {
          opacity: 0,
          y: -4,
          duration: 0.35,
          ease: 'power2.out',
          stagger: 0.05,
        })
  
        gsap.to(this.#reviewList, {
          height: 'auto',
          duration: 0.85,
          ease: 'elastic.out(1, 1.25)',
        })
      })

      return;
    }

    if (removeExisting) {
      gsap.to('.product-reviews__review', {
        opacity: 0,
        y: -4,
        duration: 0.35,
        ease: 'power2.out',
        stagger: 0.05,
        onComplete: () => {
          gsap.set(this.#reviewList, { height: this.#reviewList.offsetHeight });

          requestAnimationFrame(() => {
            this.renderReviews(removeExisting);

            gsap.to(this.#reviewList, {
              height: 'auto',
              duration: 0.85,
              ease: 'elastic.out(1, 1.25)',
            })
    
            gsap.from('.product-reviews__review', {
              opacity: 0,
              y: 4,
              duration: 0.45,
              ease: 'power3.out',
              stagger: 0.05,
            })
          })
        }
      })
    } else {
      this.renderReviews(removeExisting);

      gsap.to('.product-reviews__review', {
        height: 'auto',
        duration: 0.65,
        ease: 'expo.out',
      })
    }

  }

  getQueryParams() {
    let queryParams = `?per_page=${MAX_PER_PAGE}`

    if (this.#sortBy === 'rating-asc') {
      queryParams += '&sort=rating&direction=asc'
    } else if (this.#sortBy === 'rating-desc') {
      queryParams += '&sort=rating'
    } else {
      queryParams += `&sort=${this.#sortBy}`
    }

    queryParams += `&page=${this.#currentPage}`

    if (this.#filter !== 'all') {
      queryParams += `&star=${this.#filter}`;
    }

    return queryParams
  }

  async fetchReviews(removeExisting = true) {
    if (!this.#productId) return;

    const queryParams = this.getQueryParams();

    const endpoint = `https://api.yotpo.com/v1/widget/${store_id}/products/${this.#productId}/reviews.json${queryParams}`

    try {
      const response = await fetch(endpoint);
      
      const data = await response.json();

      if (!data?.response || data?.status?.code !== 200) {
        throw new Error('Failed to fetch reviews from Yotpo');
      }

      console.log(data);

      this.#data = {
        total: data.response.bottomline?.total_review ?? 0,
        average: data.response.bottomline?.average_score ?? 0,
        reviews: data.response.reviews ?? [],
        totalPages: (data.response.pagination ? (data.response.pagination.total / MAX_PER_PAGE) : 1),
        distribution: data.response.bottomline.star_distribution ?? {}
      }

      if (typeof data.response.pagination?.page === 'number') {
        this.#currentPage = data.response.pagination?.page;
      }

      this.render(removeExisting);
    } catch (error) {
      console.error(error);
    }
  }

  connectedCallback() {
    this.#productId = this.dataset.productId;
    this.#reviewCount = this.querySelector(selectors.reviewCount);
    this.#template = this.querySelector(selectors.template);
    this.#reviewList = this.querySelector(selectors.reviewList);
    this.#reviewSort = this.querySelector(selectors.reviewSort);
    this.#loadMoreButton = this.querySelector(selectors.loadMoreButton);
    this.#loadMoreContainer = this.querySelector(selectors.loadMoreContainer);
    this.#reviewFilter = this.querySelector(selectors.reviewFilter);
    this.#reviewFilterLabel = this.querySelector(selectors.reviewFilterLabel);

    this.fetchReviews();

    this.#reviewSort?.addEventListener('change', this.handleSortChange, { passive: true });
    this.#loadMoreButton?.addEventListener('click', this.handleLoadMore, { passive: true });
    this.#reviewFilter?.addEventListener('change', this.handleFilterChange, { passive: true });
  }

  disconnectedCallback() {
    this.#reviewSort?.removeEventListener('change', this.handleSortChange);
    this.#loadMoreButton?.removeEventListener('click', this.handleLoadMore);
    this.#reviewFilter?.removeEventListener('change', this.handleFilterChange);
  }
}

if (!customElements.get('product-reviews')) {
  customElements.define('product-reviews', ProductReviews);
}